import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box } from '@mui/material';
import { usePendingSignatureCount } from 'components/@home/@signatures/pendingSignatures';
import BrowserNotifications from 'components/@home/drawers/SettingsDrawer/SettingsForm/BrowserNotifications';
import MobileNotifications from 'components/@home/drawers/SettingsDrawer/SettingsForm/MobileNotifications';
import WorkingDays from 'components/@home/drawers/SettingsDrawer/SettingsForm/WorkingDays';
import cloneDeep from 'lodash/cloneDeep';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useSelector } from 'react-redux';
import auth from 'store/app/auth/action';
import employees from 'store/app/entities/employees/action';
import settingsAction from 'store/app/entities/settings/action';
import devicesDrawer from 'store/app/ui/drawers/devices/action';
import passkeyManagementDrawer from 'store/app/ui/drawers/passkeyManagement/action';
import settingsDrawer from 'store/app/ui/drawers/settings/action';
import authCompany from 'store/selectors/authCompany';
import authEmployee from 'store/selectors/authEmployee';
import authUser from 'store/selectors/authUser';
import currentEmployee from 'store/selectors/currentEmployee';
import { I18n } from 'utils/i18n';
import useActions from 'utils/useActions';
import { useDebounceCallback } from 'utils/useDebounceCallback';
import { isWebAuthnSupported } from 'utils/webAuthn';
import Company from './Company';
import Employee from './Employee';
import Item from './Item';
import Language from './Language';
import TimeZone from './TimeZone';
import Title from './Title';

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    minWidth: 455,
  },
  wrapper: {
    overflow: 'auto',
    padding: 2,
  },
  section: {
    marginTop: 5,
  },
  account: {
    paddingRight: '20px',
  },
  headline: {
    fontSize: 28,
    fontWeight: 'bold',
    fontStyle: 'normal',
    fontStretch: 'normal',
    lineHeight: '38px',
    marginTop: 1,
    fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
    letterSpacing: '-0.7px',
    color: 'primary.ultraDark',
    padding: 2,
  },
  chevron: {
    fontSize: 18,
    color: 'text.secondary',
  },
};

const DELAY = 1000;

const SettingsForm = () => {
  const pendingSignatures = usePendingSignatureCount();
  const company = useSelector(authCompany);
  const { _id } = useSelector(authEmployee);
  const user = useSelector(authUser);
  const passkeyEnabled = user?.webAuthnCredentials?.length > 0;
  const storeEmployee = useSelector(currentEmployee);
  const employee = useMemo(() => cloneDeep(storeEmployee), [storeEmployee]);
  const [changed, setChanged] = useState(null);
  const [
    settingsUpdate,
    employeesModify,
    logout,
    openPasskeyManagement,
    openDevices,
    closeSettings,
  ] = useActions([
    settingsAction.update,
    employees.modify,
    auth.logout,
    passkeyManagementDrawer.open,
    devicesDrawer.open,
    settingsDrawer.close,
  ]);
  const navigate = useNavigate();

  const settingsUpdateDebounced = useDebounceCallback(
    useCallback(
      (...args) => {
        settingsUpdate(...args);
        setChanged(null);
      },
      [settingsUpdate],
    ),
    DELAY,
  );

  const settings = useMemo(() => ({ ...employee.settings, ...changed }), [employee, changed]);

  useEffect(() => {
    if (changed) {
      settingsUpdateDebounced(_id, changed);
    }
  }, [_id, settings, settingsUpdateDebounced, changed]);

  const changeEmployeeSettings = useCallback(
    update => {
      employeesModify(_id, {
        settings: {
          ...settings,
          ...update,
        },
      });
      setChanged(c => {
        const newVar = { ...c, ...update };
        console.log('------', c, update, newVar);
        return newVar;
      });
    },
    [employeesModify, _id, settings, setChanged],
  );

  const changeNotifications = useCallback(
    value => {
      const update = {
        isOpenCommunication: value === 'Always',
        dnd: value === 'Never',
      };
      changeEmployeeSettings(update);
    },
    [changeEmployeeSettings],
  );

  const changeBrowserNotifications = useCallback(
    value => {
      changeEmployeeSettings({ browser: value });
    },
    [changeEmployeeSettings],
  );

  const changeLanguage = useCallback(
    event => {
      changeEmployeeSettings({
        language: event.value,
      });
    },
    [changeEmployeeSettings],
  );

  const changeTimeZone = useCallback(
    event => {
      changeEmployeeSettings({
        timeZone: event.value,
      });
    },
    [changeEmployeeSettings],
  );

  const changeWorkingDays = useCallback(
    workingDays => {
      changeEmployeeSettings({
        workingDays,
      });
    },
    [changeEmployeeSettings],
  );

  const handleOpenPasskeyManagement = useCallback(() => {
    openPasskeyManagement();
  }, [openPasskeyManagement]);

  const handleOpenDevicesDrawer = useCallback(() => {
    openDevices();
  }, [openDevices]);

  // Check if WebAuthn is supported in the browser
  const isPasskeySupported = isWebAuthnSupported();

  // Get passkey status text based on availability and whether it's enabled
  const getPasskeyStatusText = () => {
    if (!isPasskeySupported) {
      return I18n.t('SettingsDrawer.Passkey_Not_Available', 'Not available on this device');
    }

    if (passkeyEnabled) {
      return I18n.t('SettingsDrawer.Passkey_Enabled', 'Passkey enabled');
    }

    return I18n.t('SettingsDrawer.Passkey_Disabled', 'Passkey disabled');
  };

  return (
    <Box component="form" sx={styles.root}>
      <Box sx={styles.headline}>{I18n.t('SettingsDrawer.Settings')}</Box>
      <Box sx={styles.wrapper}>
        <Box>
          <Employee name={employee.firstName} surname={employee.surName} />
          <Company headline={company.name} />
          {employee.code ? <Item headline={employee.code} /> : null}
          <Item headline={employee.jobTitle} />
          <Item headline={employee.department} />
        </Box>
        <Box sx={styles.section}>
          <Title headline={I18n.t('SettingsDrawer.Notifications')} />
          <BrowserNotifications
            settings={settings.browser || { sound: true }}
            onChange={changeBrowserNotifications}
          />
          <MobileNotifications settings={settings} onChange={changeNotifications} />
          {!settings.dnd && !settings.isOpenCommunication ? (
            <WorkingDays value={settings.workingDays} onChange={changeWorkingDays} />
          ) : null}
        </Box>
        <Box sx={styles.section}>
          <Title headline={I18n.t('SettingsDrawer.Language & Region')} />
          <Language onChange={changeLanguage} />
          <TimeZone onChange={changeTimeZone} />
        </Box>
        <Box sx={styles.section}>
          <Box sx={styles.account}>
            <Title headline={I18n.t('SettingsDrawer.Account')} />
            <Item
              headline={I18n.t('SettingsDrawer.Device_Management', 'Device Management')}
              onClick={handleOpenDevicesDrawer}
              sx={{ paddingRight: 0 }}
            >
              <ChevronRightIcon sx={styles.chevron} />
            </Item>
            <Item
              headline={I18n.t('SettingsDrawer.Secure_Account', 'Secure your account')}
              onClick={handleOpenPasskeyManagement}
              statusText={getPasskeyStatusText()}
              statusEnabled={passkeyEnabled}
              sx={{ paddingRight: 0 }}
            >
              <ChevronRightIcon sx={styles.chevron} />
            </Item>
            <Item
              headline={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {I18n.t('SettingsDrawer.My signatures')}
                  {pendingSignatures > 0 ? (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        flex: 'none',
                      }}
                    />
                  ) : null}
                </Box>
              }
              onClick={() => {
                closeSettings();
                navigate('/home/signatures');
              }}
            />
            <Item headline={I18n.t('SettingsDrawer.Logout')} onClick={logout} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default SettingsForm;
